import React from 'react';

/**
 * Skeleton - 統一的骨架載入組件
 * 使用 Tailwind animate-pulse，不需要自訂 keyframes
 */

// 基礎骨架樣式
const baseClass = 'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%] animate-pulse';

/**
 * 基礎骨架線 - 用於文字
 */
export function SkeletonLine({
  width = '100%',
  height = '16px',
  className = ''
}) {
  return (
    <div
      className={`${baseClass} rounded ${className}`}
      style={{ width, height }}
    />
  );
}

/**
 * 圓形骨架 - 用於頭像
 */
export function SkeletonCircle({
  size = '40px',
  className = ''
}) {
  return (
    <div
      className={`${baseClass} rounded-full shrink-0 ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

/**
 * 矩形骨架 - 用於卡片、圖片
 */
export function SkeletonCard({
  width = '100%',
  height = '200px',
  className = ''
}) {
  return (
    <div
      className={`${baseClass} rounded-xl ${className}`}
      style={{ width, height }}
    />
  );
}

/**
 * 骨架容器 - 用於包裝多個骨架元素
 */
export function SkeletonContainer({
  children,
  className = ''
}) {
  return (
    <div className={`space-y-3 ${className}`}>
      {children}
    </div>
  );
}

/**
 * 文字區塊骨架 - 模擬多行文字
 */
export function SkeletonText({
  lines = 3,
  className = ''
}) {
  const widths = ['100%', '95%', '90%', '85%', '75%'];
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonLine
          key={i}
          width={widths[i % widths.length]}
          height="14px"
        />
      ))}
    </div>
  );
}

/**
 * 列表項骨架 - 用於列表
 */
export function SkeletonListItem({ className = '' }) {
  return (
    <div className={`flex items-center gap-3 py-3 ${className}`}>
      <SkeletonCircle size="40px" />
      <div className="flex-1 space-y-2">
        <SkeletonLine width="60%" height="14px" />
        <SkeletonLine width="40%" height="12px" />
      </div>
    </div>
  );
}

/**
 * 表格行骨架
 */
export function SkeletonTableRow({ columns = 4, className = '' }) {
  return (
    <div className={`flex items-center gap-4 py-3 ${className}`}>
      {Array.from({ length: columns }).map((_, i) => (
        <SkeletonLine
          key={i}
          width={`${100 / columns}%`}
          height="16px"
        />
      ))}
    </div>
  );
}

// ============ 頁面專用骨架 ============

/**
 * Dashboard 骨架
 */
export function DashboardSkeleton({ isMobile = false }) {
  return (
    <div className={`min-h-screen bg-[var(--bg-primary)] ${isMobile ? 'p-4' : 'p-8'}`}>
      {/* Header */}
      <div className={`${isMobile ? 'mb-6' : 'mb-8'}`}>
        <SkeletonLine width={isMobile ? '180px' : '250px'} height={isMobile ? '24px' : '28px'} className="mb-2" />
        <SkeletonLine width={isMobile ? '200px' : '300px'} height="15px" />
      </div>

      {/* Time Range Selector */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
        <div className={`flex gap-4 ${isMobile ? 'flex-col' : 'flex-row'}`}>
          <SkeletonLine width={isMobile ? '100%' : '200px'} height="40px" />
          <SkeletonLine width={isMobile ? '100%' : '150px'} height="40px" />
          <SkeletonLine width={isMobile ? '100%' : '150px'} height="40px" />
        </div>
      </div>

      {/* Account Card */}
      <div className={`bg-[var(--bg-secondary)] ${isMobile ? 'rounded-xl p-5' : 'rounded-2xl p-8'} shadow-sm mb-6`}>
        {/* Account Header */}
        <div className="flex items-start gap-4 mb-8">
          <SkeletonCircle size={isMobile ? '48px' : '60px'} />
          <div className="flex-1">
            <SkeletonLine width="200px" height="24px" className="mb-2" />
            <SkeletonLine width="120px" height="16px" />
          </div>
        </div>

        {/* Stats Grid */}
        <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-3'} gap-4 mb-8`}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <SkeletonLine width="80px" height="16px" className="mb-2" />
              <SkeletonLine width="60px" height="28px" />
            </div>
          ))}
        </div>

        {/* Chart */}
        <SkeletonCard height={isMobile ? '250px' : '300px'} />
      </div>

      {/* Recent Card */}
      <div className={`bg-[var(--bg-secondary)] ${isMobile ? 'rounded-xl p-5' : 'rounded-2xl p-8'} shadow-sm`}>
        <SkeletonLine width="180px" height="24px" className="mb-4" />
        <SkeletonCard height="200px" />
      </div>
    </div>
  );
}

/**
 * Posts 頁面骨架
 */
export function PostsPageSkeleton({ isMobile = false }) {
  return (
    <div className={`${isMobile ? 'p-4' : 'p-8'} max-w-6xl mx-auto`}>
      {/* Title */}
      <SkeletonLine width={isMobile ? '150px' : '200px'} height={isMobile ? '24px' : '32px'} className="mb-8" />

      {/* Filter Tabs */}
      <div className="flex gap-4 mb-8 flex-wrap">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonLine key={i} width="100px" height="40px" />
        ))}
      </div>

      {/* Posts Grid */}
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-[repeat(auto-fill,minmax(300px,1fr))]'} gap-6`}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between mb-4">
              <SkeletonLine width="100px" height="20px" />
              <SkeletonLine width="60px" height="24px" />
            </div>
            <SkeletonText lines={3} className="mb-4" />
            <div className="flex gap-2">
              <SkeletonLine width="80px" height="32px" />
              <SkeletonLine width="80px" height="32px" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Profile 頁面骨架
 */
export function ProfilePageSkeleton({ isMobile = false }) {
  return (
    <div className={`${isMobile ? 'p-4' : 'p-8'} max-w-6xl mx-auto`}>
      {/* Title */}
      <SkeletonLine width={isMobile ? '150px' : '200px'} height={isMobile ? '24px' : '32px'} className="mb-8" />

      {/* Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <SkeletonLine width="150px" height="24px" className="mb-6" />
        <div className="space-y-4">
          <SkeletonLine width="100%" height="48px" />
          <SkeletonLine width="100%" height="48px" />
          <SkeletonLine width="100%" height="48px" />
        </div>
      </div>

      {/* Subscription Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <SkeletonLine width="150px" height="24px" className="mb-6" />
        <SkeletonLine width="200px" height="32px" className="mb-4" />
        <SkeletonText lines={2} className="mb-6" />
        <SkeletonLine width="120px" height="40px" />
      </div>
    </div>
  );
}

/**
 * 通用頁面骨架
 */
export function PageSkeleton({ cards = 3, showTitle = true }) {
  return (
    <div className="p-8 max-w-6xl mx-auto">
      {showTitle && (
        <>
          <SkeletonLine width="250px" height="36px" className="mb-2" />
          <SkeletonLine width="400px" height="20px" className="mb-8" />
        </>
      )}
      <div className="space-y-4">
        {Array.from({ length: cards }).map((_, i) => (
          <SkeletonCard key={i} height="200px" />
        ))}
      </div>
    </div>
  );
}

export default {
  Line: SkeletonLine,
  Circle: SkeletonCircle,
  Card: SkeletonCard,
  Container: SkeletonContainer,
  Text: SkeletonText,
  ListItem: SkeletonListItem,
  TableRow: SkeletonTableRow,
  Dashboard: DashboardSkeleton,
  PostsPage: PostsPageSkeleton,
  ProfilePage: ProfilePageSkeleton,
  Page: PageSkeleton,
};
